/**
 * Tank List Editor Component
 * Editable list of fuel/oil tanks for aircraft configuration.
 *
 * Each tank has: name, capacity, unit.
 * Oil is modelled as just another tank (e.g. name "Oil", unit "quarts").
 *
 * Config shape:
 *   { tanks: [ { name: "Left Wing", capacity: 100, unit: "litres" }, ... ] }
 *
 * @module tank-list-editor
 */

const UNIT_OPTIONS = [
  // Fuel volume/mass units
  { value: 'usg',    label: 'US Gallons (USG)' },
  { value: 'impgal', label: 'Imp Gallons' },
  { value: 'litres', label: 'Litres (L)' },
  { value: 'lb',     label: 'Pounds (lb)' },
  { value: 'kg',     label: 'Kilograms (kg)' },
  // Oil-specific units
  { value: 'quarts', label: 'Quarts' },
  { value: 'pints',  label: 'Pints' },
];

/**
 * Parse a fuel_tank_config into the canonical array format.
 * @param {Object|null} config
 * @returns {{ name: string, capacity: number, unit: string }[]}
 */
export function parseTankConfig(config) {
  if (!config) return [];
  if (Array.isArray(config.tanks) && config.tanks.length > 0 && typeof config.tanks[0] === 'object') {
    return config.tanks.map(t => ({
      name: t.name || '',
      capacity: Number(t.capacity) || 0,
      unit: t.unit || 'litres',
    }));
  }
  return [];
}

/**
 * Serialise tank array back to fuel_tank_config shape.
 * @param {{ name: string, capacity: number, unit: string }[]} tanks
 * @returns {Object}
 */
export function serialiseTankConfig(tanks) {
  return {
    tanks: tanks.map(t => ({
      name: t.name.trim(),
      capacity: Number(t.capacity) || 0,
      unit: t.unit || 'litres',
    })).filter(t => t.name),
  };
}

/**
 * Create a tank list editor.
 *
 * @param {Object} [options]
 * @param {Object} [options.config] - Existing fuel_tank_config to pre-populate
 * @returns {HTMLElement} Container element with .getTankConfig() method
 */
export function createTankListEditor({ config } = {}) {
  const tanks = parseTankConfig(config);

  const container = document.createElement('div');
  container.className = 'tank-list-editor';

  const listEl = document.createElement('div');
  listEl.className = 'tank-list-editor__list';
  container.appendChild(listEl);

  function renderRow(tank = { name: '', capacity: 0, unit: 'litres' }) {
    const row = document.createElement('div');
    row.className = 'tank-list-editor__row';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'tank-list-editor__name';
    nameInput.placeholder = 'Tank name';
    nameInput.value = tank.name;
    row.appendChild(nameInput);

    const capInput = document.createElement('input');
    capInput.type = 'number';
    capInput.className = 'tank-list-editor__capacity';
    capInput.placeholder = 'Max';
    capInput.min = '0';
    capInput.step = '0.1';
    capInput.value = tank.capacity || '';
    row.appendChild(capInput);

    const unitSelect = document.createElement('select');
    unitSelect.className = 'tank-list-editor__unit';
    unitSelect.innerHTML = UNIT_OPTIONS.map(u =>
      `<option value="${u.value}"${u.value === tank.unit ? ' selected' : ''}>${u.label}</option>`
    ).join('');
    row.appendChild(unitSelect);

    const isOil = tank.name.toLowerCase() === 'oil';

    if (!isOil) {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'btn btn--danger btn--sm tank-list-editor__remove';
      removeBtn.textContent = 'X';
      removeBtn.title = 'Remove tank';
      removeBtn.addEventListener('click', () => row.remove());
      row.appendChild(removeBtn);
    }

    // Lock the Oil name field so it can't be renamed
    if (isOil) {
      nameInput.readOnly = true;
    }

    listEl.appendChild(row);
    return row;
  }

  // Render existing tanks, or seed with Oil as default first entry
  if (tanks.length > 0) {
    tanks.forEach(t => renderRow(t));
  } else {
    renderRow({ name: 'Oil', capacity: 0, unit: 'quarts' });
  }

  // Add button
  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'btn tank-list-editor__add';
  addBtn.textContent = '+ Add Tank';
  addBtn.addEventListener('click', () => renderRow());
  container.appendChild(addBtn);

  /**
   * Collect current tank data from the DOM.
   * @returns {Object} fuel_tank_config object
   */
  container.getTankConfig = function () {
    const rows = listEl.querySelectorAll('.tank-list-editor__row');
    const result = [];
    for (const row of rows) {
      const name = row.querySelector('.tank-list-editor__name').value.trim();
      const capacity = parseFloat(row.querySelector('.tank-list-editor__capacity').value) || 0;
      const unit = row.querySelector('.tank-list-editor__unit').value;
      if (name) {
        result.push({ name, capacity, unit });
      }
    }
    return serialiseTankConfig(result);
  };

  return container;
}
